import { getHeaders, PURCHASER_API_BASE_URL } from "../Constants";
import type { Label } from "./Labels.ts";

/**
 * NOTE: Copied from the API
 *
 * Supported data types for cell values
 */
export type CellDataType = "text" | "object" | "number" | "currency" | "date" | "boolean" | "select";

/**
 * NOTE: Copied from the API
 *
 * Column alignment options for display
 */
export type ColumnAlignment = "left" | "center" | "right";

/**
 * NOTE: Copied from the API
 *
 * Column definition structure stored in JSONB
 *
 * Each column has a unique nanoid, display properties, and data type information.
 * Columns maintain their own order property for flexible reordering.
 */
export type ColumnDefinition = {
    /** Unique identifier (nanoid) for the column */
    id: string;
    /** Display order of the column */
    order: number;
    /** Human-readable name for the column */
    name: string;
    /** Data type for values in this column */
    dataType?: CellDataType;
    /** Text alignment for display */
    alignment?: ColumnAlignment;
    /** Display width in pixels */
    width?: number;
    /** Whether this column is required (future validation) */
    required?: boolean;
    /** Whether this column should be displayed to a user or exported */
    hidden?: boolean;
    /** Available options for select type columns */
    options?: string[];
};

/**
 * NOTE: Copied from the API
 *
 * Individual cell value with optional type override
 *
 * Cells can override their column's data type if needed,
 * allowing for flexible data entry.
 */
export type CellValue = {
    /** The actual value stored in the cell */
    value: string | number | boolean | object | null;
    /** Optional override for the column's dataType */
    dataType?: CellDataType;
};

/**
 * NOTE: Copied from the API
 *
 * Row data structure mapping column IDs to cell values
 *
 * Uses column IDs as keys for efficient lookups and
 * maintains data integrity even when columns are reordered.
 */
export type RowData = {
    [columnId: string]: CellValue;
};

// Default columns for backwards compatibility
export const DEFAULT_COLUMN_DEFINITIONS: ColumnDefinitionInput[] = [
    { name: "name", dataType: "text" },
    { name: "description", dataType: "text" },
];

// Row from tabular data store
export type TabularRow = {
    id: number;
    order: number;
    cellValues: RowData;
    version: number;
    createdAt: string;
    updatedAt: string;
};

// Tabular data store
export type TabularDataStore = {
    id: number;
    name: string;
    description: string | null;
    columnDefinitions: ColumnDefinition[];
    rows: TabularRow[];
    version: number;
    createdAt: string;
    updatedAt: string;
};

// Label assignment
export type CatalogLabelAssignment = {
    id: number;
    createdAt: string;
    catalogItemId: number;
    labelId: number;
    label: Label;
};

// File attachment
export type CatalogItemAttachment = {
    id: number;
    createdAt: string;
    catalogItemId: number;
    fileId: number;
    file: {
        id: number;
        originalFilename: string;
        cleanFilename: string;
        mimeType: string;
        fileSizeBytes: number;
        createdAt: string;
    };
};

// User info included in responses
export type CatalogItemUser = {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
};

// Catalog item from list endpoint (without tabularDataStore)
export type CatalogItemSummary = {
    id: number;
    name: string;
    description: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    teamId: number;
    createdByUserId: number;
    tabularDataStoreId: number;
    createdByUser: CatalogItemUser;
    labelAssignments: CatalogLabelAssignment[];
    attachments: CatalogItemAttachment[];
    preferredVendor: {
        id: number;
        name: string | null;
    } | null;
    lastRfq: {
        id: number;
        uniquePublicId: string;
        title: string | null;
    } | null;
    lastQuoted: {
        uniquePublicId: string;
        mailboxId: number;
        revision: number;
        title: string | null;
    } | null;
    currentRequest: {
        id: number;
        name: string;
    } | null;
    lastVendor: {
        id: number;
        name: string | null;
    } | null;
};

// Full catalog item with tabular data store
export type CatalogItem = CatalogItemSummary & {
    tabularDataStore: TabularDataStore;
};

// Filter parameters for list endpoint
export type CatalogFilterParams = {
    search?: string;
    sortBy?: "name" | "updatedAt" | "createdAt";
    sortOrder?: "asc" | "desc";
    labelIds?: number[];
    limit?: number;
    offset?: number;
};

// Column definition input for create/update
export type ColumnDefinitionInput = {
    id?: string;
    name: string;
    dataType: CellDataType;
};

// Create/Update payload
export type CatalogItemPayload = {
    name: string;
    description?: string;
    columnDefinitions?: ColumnDefinitionInput[];
    lineItemData: RowData;
};

export type CatalogItemUpdatePayload = {
    name?: string;
    description?: string;
    preferredVendorId?: number | null;
    columnDefinitions?: ColumnDefinitionInput[];
    lineItemData?: RowData;
};

// Add to RFQ payload
export type AddToRFQPayload = {
    catalogItemIds: number[];
};

export type CreateRFQFromCatalogPayload = AddToRFQPayload & {
    title?: string;
};

// History query parameters
export type CatalogItemHistoryParams = {
    limit?: number;
    offset?: number;
    startDate?: Date;
    endDate?: Date;
};

// History entry types
export type CatalogItemHistoryEntry = {
    id: number;
    createdAt: Date;
    currencySymbol: string | null;
    quantity: number | null;
    unitOfMeasure: string | null;
    unitPrice: number | null;
    totalPrice: number | null;
    leadTime: string | null;
    rfq: {
        id: number;
        uniquePublicId: string;
        title: string | null;
    };
    quote: {
        uniquePublicId: string;
        mailboxId: number;
        revision: number;
        title: string | null;
    };
    vendor: {
        id: number;
        name: string | null;
    } | null;
};

export type CatalogItemHistoryPricingTrends = {
    averageUnitPrice: number | null;
    minUnitPrice: number | null;
    maxUnitPrice: number | null;
    priceCount: number;
    currencySymbol: string | null;
};

export type CatalogItemHistoryResponse = {
    catalogItem: {
        id: number;
        name: string;
        description: string | null;
    };
    history: CatalogItemHistoryEntry[];
    pricingTrends: CatalogItemHistoryPricingTrends;
    total: number;
};

// Helper to extract line item data from tabular data store
export function extractLineItemFromStore(store: TabularDataStore) {
    if (!store.rows || store.rows.length === 0) return null;

    const row = store.rows[0];

    return store.columnDefinitions.map(column => {
        const cell = row.cellValues[column.id];

        let parsedValue: string | number | boolean | object | null = cell.value;

        // Parse value based on data type
        if (cell.value !== null) {
            switch (column.dataType) {
                case "number":
                case "currency":
                    parsedValue = parseFloat(cell.value as string) || null;
                    break;
                case "boolean":
                    parsedValue = cell.value === "true";
                    break;
                case "object":
                    try {
                        parsedValue = JSON.parse(cell.value as string);
                    } catch {
                        parsedValue = null;
                    }
                    break;
                // text, date, select remain as strings
            }
        }

        return {
            ...column,
            value: parsedValue,
        };
    });
}

function buildQueryString(filters?: CatalogFilterParams): string {
    if (!filters) return "";

    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.sortBy) params.set("sortBy", filters.sortBy);
    if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);
    if (filters.labelIds && filters.labelIds.length > 0) {
        params.set("labelIds", filters.labelIds.join(","));
    }
    if (filters.limit !== undefined) params.set("limit", String(filters.limit));
    if (filters.offset !== undefined) params.set("offset", String(filters.offset));

    const queryString = params.toString();
    return queryString ? `?${queryString}` : "";
}

export default {
    // List catalog items with filtering
    getCatalogItems: async (filters?: CatalogFilterParams): Promise<CatalogItemSummary[]> => {
        const queryString = buildQueryString(filters);
        const url = PURCHASER_API_BASE_URL + "/v2/product-catalog/items" + queryString;

        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) throw new Error("Failed to get catalog items");
        return await res.json();
    },

    // Get single item with full details
    getCatalogItem: async (id: number): Promise<CatalogItem> => {
        const url = PURCHASER_API_BASE_URL + `/v2/product-catalog/items/${id}`;
        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) throw new Error("Failed to get catalog item");
        return await res.json();
    },

    // Create new catalog item
    createCatalogItem: async (payload: CatalogItemPayload): Promise<CatalogItem> => {
        const url = PURCHASER_API_BASE_URL + "/v2/product-catalog/items";
        const res = await fetch(url, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to create catalog item");
        return await res.json();
    },

    // Update existing catalog item
    updateCatalogItem: async (id: number, payload: CatalogItemUpdatePayload): Promise<CatalogItem> => {
        const url = PURCHASER_API_BASE_URL + `/v2/product-catalog/items/${id}`;
        const res = await fetch(url, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to update catalog item");
        return await res.json();
    },

    // Soft delete catalog item
    deleteCatalogItem: async (id: number): Promise<void> => {
        const url = PURCHASER_API_BASE_URL + `/v2/product-catalog/items/${id}`;
        const res = await fetch(url, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        });
        if (!res.ok) throw new Error("Failed to delete catalog item");
    },

    // Assign label to catalog item
    addCatalogItemLabel: async (itemId: number, labelId: number): Promise<CatalogLabelAssignment> => {
        const url = PURCHASER_API_BASE_URL + `/v2/product-catalog/items/${itemId}/labels`;
        const res = await fetch(url, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ labelId }),
        });
        if (!res.ok) throw new Error("Failed to add label");
        return await res.json();
    },

    // Remove label from catalog item
    removeCatalogItemLabel: async (itemId: number, labelId: number): Promise<void> => {
        const url = PURCHASER_API_BASE_URL + `/v2/product-catalog/items/${itemId}/labels`;
        const res = await fetch(url, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({ labelId }),
        });
        if (!res.ok) throw new Error("Failed to remove label");
    },

    // Attach file to catalog item
    attachFileToCatalogItem: async (itemId: number, fileId: number): Promise<CatalogItemAttachment> => {
        const url = PURCHASER_API_BASE_URL + `/v2/product-catalog/items/${itemId}/attachments`;
        const res = await fetch(url, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ fileId }),
        });
        if (!res.ok) throw new Error("Failed to attach file");
        return await res.json();
    },

    // Remove file from catalog item
    removeFileFromCatalogItem: async (itemId: number, fileId: number): Promise<void> => {
        const url = PURCHASER_API_BASE_URL + `/v2/product-catalog/items/${itemId}/attachments`;
        const res = await fetch(url, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({ fileId }),
        });
        if (!res.ok) throw new Error("Failed to remove file");
    },

    // Add catalog items to existing RFQ
    addCatalogItemsToRFQ: async (rfqPublicId: string, payload: AddToRFQPayload): Promise<void> => {
        const url = PURCHASER_API_BASE_URL + `/v2/rfqs/${rfqPublicId}/catalog-items`;
        const res = await fetch(url, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to add items to RFQ");
    },

    // Create new RFQ from catalog items
    createRFQFromCatalogItems: async (payload: CreateRFQFromCatalogPayload): Promise<{ uniquePublicId: string }> => {
        const url = PURCHASER_API_BASE_URL + "/v2/rfqs/from-catalog";
        const res = await fetch(url, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to create RFQ from catalog items");
        return await res.json();
    },

    // Get quote history for a catalog item
    getCatalogItemHistory: async (id: number, params?: CatalogItemHistoryParams): Promise<CatalogItemHistoryResponse> => {
        const queryParams = new URLSearchParams();
        if (params?.limit !== undefined) queryParams.set("limit", String(params.limit));
        if (params?.offset !== undefined) queryParams.set("offset", String(params.offset));
        if (params?.startDate) queryParams.set("startDate", params.startDate.toISOString());
        if (params?.endDate) queryParams.set("endDate", params.endDate.toISOString());

        const queryString = queryParams.toString();
        const url = PURCHASER_API_BASE_URL + `/v2/product-catalog/items/${id}/history` + (queryString ? `?${queryString}` : "");
        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) throw new Error("Failed to get catalog item history");
        return await res.json();
    },
};
